/* eslint-disable @typescript-eslint/no-non-null-assertion */
import {
  AfterContentInit,
  ContentChildren,
  Directive,
  Input,
  OnDestroy,
  OnInit,
  QueryList,
} from '@angular/core';
import { FormGroupDirective } from '@angular/forms';
import { forEach, get, isNil, reduce } from 'lodash';
import {
  BehaviorSubject,
  Observable,
  Subject,
  filter,
  map as rxMap,
  startWith,
  switchMap,
  takeUntil,
} from 'rxjs';

import { PortalFormConfig } from '@lockton-apps/shared/interfaces';
import { STATIC_PORTAL_CONFIG } from '@lockton-apps/shared/util/constants';

import { ConfigResolverService } from '../config-resolver.service';
import { FormPortalConfigFieldDirective } from './form-portal-config-field.directive';

STATIC_PORTAL_CONFIG;

@Directive({
    selector: '[laFormPortalConfig]',
    standalone: false
})
export class FormPortalConfigDirective
  implements OnInit, AfterContentInit, OnDestroy
{
  @ContentChildren(FormPortalConfigFieldDirective, { descendants: true })
  fields!: QueryList<FormPortalConfigFieldDirective>;

  @Input() get portalId() {
    return this.portalId$.value;
  }
  set portalId(value: number | null | undefined) {
    this.portalId$.next(value);
  }
  private portalId$ = new BehaviorSubject<number | null | undefined>(null);

  @Input() formId!: string;

  private config$!: Observable<PortalFormConfig | null | undefined>;

  private readonly destroyed$ = new Subject<void>();

  constructor(
    private readonly formDirective: FormGroupDirective,
    private readonly configResolver: ConfigResolverService
  ) {}

  ngOnInit(): void {
    if (this.formId) {
      this.config$ = this.portalId$.pipe(
        filter((portalId) => !isNil(portalId)),
        switchMap((portalId) =>
          this.configResolver.resolveFormConfig(portalId!, this.formId)
        )
      );
    }
  }

  ngAfterContentInit(): void {
    this.config$
      .pipe(
        switchMap((config) =>
          this.fields.changes.pipe(
            startWith(this.fields),
            rxMap((fields) => ({
              config,
              fields,
            }))
          )
        ),
        takeUntil(this.destroyed$)
      )
      .subscribe(({ config }) => {
        if (config) {
          this.applyFieldConfig(config);
          this.applyParsers(config);
        }
      });
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  applyFieldConfig(config: PortalFormConfig) {
    forEach(this.fields.toArray(), (field) => {
      if (field instanceof FormPortalConfigFieldDirective) {
        const fieldConfig = get(config.fields, field.fieldKey);

        if (fieldConfig) {
          field.applyConfig(fieldConfig);
        }
      }
    });
  }

  applyParsers(config: PortalFormConfig) {
    if (isNil(config.parsers) || !config.parsers.length) {
      return;
    }

    this.formDirective.form.valueChanges
      .pipe(
        rxMap((value) => {
          return reduce(
            config.parsers,
            (parsedVal, parser) => parser(parsedVal),
            value
          );
        }),
        takeUntil(this.destroyed$)
      )
      .subscribe((parsedValue) => {
        this.formDirective.form.patchValue(parsedValue, { emitEvent: false });
      });
  }
}
